/**
 * GLSL (WebGL2, #version 300 es) for the LightLayer.
 *
 * Passes (one frame, when anything is lit):
 *   prerender  GLOW   instanced capsules → quarter-res buffer, blendEquation(MAX)
 *              LINE   instanced capsules → full-res MRT {core premultiplied, shade coverage}, MAX
 *   render     GLOW composite  screen: blendFuncSeparate(ONE, ONE_MINUS_SRC_COLOR, ZERO, ONE), × --ld-glow-gain
 *              LINE composite  core over shade, premultiplied over (ONE, ONE_MINUS_SRC_ALPHA)
 *              COMET sprites   premultiplied over; the halo is emitted with low alpha so it adds light
 *
 * MAX blending inside each offscreen buffer is what keeps overlapping capsules
 * (round joins, 100 routes sharing the access road) from summing: alpha is
 * constant along a line and never beads at a join.
 *
 * Geometry: every segment is one instance of a 4-vertex strip. The vertex
 * shader clips the segment to the camera near plane and to the visible metres
 * [tail, head], projects both ends to device pixels and expands a screen-space
 * capsule (constant pixel width under pitch). The fragment shader measures the
 * capsule distance (round joins and caps, 1 device-px AA), and recovers the
 * metres at the pixel with perspective-correct interpolation (w of both ends).
 *
 * Numbers mirrored on the CPU in color.ts (trail, packet) and state.ts (layout).
 */
import { PACKET_BRIGHTEN, PACKET_WIDEN_PX } from "./color";

/** Glow Gaussian σ as a multiple of the core width (CSS px). Visible falloff (≈ 2σ each side) spans ≈ 6× the width. */
export const GLOW_SIGMA_PER_WIDTH = 1.4;
/** Upper bound (device px) of the shaded gap the line composite closes between two cores (L1-15). */
export const BRIDGE_MAX_PX = 16;
/** Minimum glow σ in CSS px, so thin bloom lines still carry a halo of light. */
export const GLOW_SIGMA_MIN_PX = 2.4;
/** Peak glow intensity at the centre line before --ld-glow-gain. */
export const GLOW_PEAK = 0.75;
/** Comet halo: brightness and how much of the land it occludes (0 = pure additive light). */
export const HALO_INTENSITY = 0.85;
export const HALO_OCCLUSION = 0.28;

const f = (n: number): string => (Number.isInteger(n) ? `${n}.0` : String(n));

const HEADER = /* glsl */ `#version 300 es
precision highp float;
precision highp int;
precision highp sampler2D;
`;

/** State texel layout (state.ts): s0 head tail alpha pulse · s1 sigma width glow style · s2 trail comet cometScale drawnLen. */
const CAPSULE_VS = (glow: boolean) => /* glsl */ `${HEADER}
layout(location = 0) in vec2 a_a;
layout(location = 1) in vec2 a_b;
layout(location = 2) in vec2 a_m;
layout(location = 3) in float a_path;

uniform mat4 u_matrix;      // origin-relative Mercator → clip (float64-built, see matrix.ts)
uniform vec2 u_viewport;    // full-resolution drawing buffer, device px
uniform float u_dpr;
uniform float u_near;       // smallest clip w kept (camera near plane)
uniform float u_shadePx;    // CSS px added to the core for the contact shade (0 = none)
uniform float u_glowSigma;  // σ per CSS px of core width
uniform float u_glowMin;    // σ floor, CSS px
uniform sampler2D u_state;

out vec2 v_px;
flat out vec4 v_seg;  // visible segment ends in device px: lo.xy, hi.xy
flat out vec4 v_wm;   // w at lo, w at hi, metres at lo, metres at hi
flat out vec4 v_s0;
flat out vec4 v_s1;
flat out vec4 v_s2;

void cull() {
  gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
  v_px = vec2(0.0);
  v_seg = vec4(0.0);
  v_wm = vec4(1.0, 1.0, 0.0, 0.0);
  v_s0 = vec4(0.0);
  v_s1 = vec4(0.0);
  v_s2 = vec4(0.0);
}

void main() {
  int row = int(a_path + 0.5);
  vec4 s0 = texelFetch(u_state, ivec2(0, row), 0);
  vec4 s1 = texelFetch(u_state, ivec2(1, row), 0);
  vec4 s2 = texelFetch(u_state, ivec2(2, row), 0);
  float head = s0.x, tail = s0.y, alpha = s0.z;
  float mA = a_m.x, mB = a_m.y;
  ${glow ? "if (s1.z <= 0.0005) { cull(); return; }" : ""}
  if (alpha <= 0.0005 || head <= tail || mB <= tail || mA >= head || s1.y <= 0.0) { cull(); return; }

  vec4 ca = u_matrix * vec4(a_a, 0.0, 1.0);
  vec4 cb = u_matrix * vec4(a_b, 0.0, 1.0);
  if (ca.w < u_near && cb.w < u_near) { cull(); return; }
  if (ca.w < u_near) {
    float k = (u_near - ca.w) / (cb.w - ca.w);
    ca = mix(ca, cb, k);
    mA = mix(mA, mB, k);
  } else if (cb.w < u_near) {
    float k = (u_near - cb.w) / (ca.w - cb.w);
    cb = mix(cb, ca, k);
    mB = mix(mB, mA, k);
  }

  // Only the drawn metres [tail, head]: clip-space lerp is exact for ground fractions.
  float len = max(mB - mA, 1e-4);
  float sLo = clamp((tail - mA) / len, 0.0, 1.0);
  float sHi = clamp((head - mA) / len, 0.0, 1.0);
  if (sHi <= sLo) { cull(); return; }
  vec4 cLo = mix(ca, cb, sLo);
  vec4 cHi = mix(ca, cb, sHi);
  float mLo = mix(mA, mB, sLo), mHi = mix(mA, mB, sHi);

  vec2 pLo = (cLo.xy / cLo.w * 0.5 + 0.5) * u_viewport;
  vec2 pHi = (cHi.xy / cHi.w * 0.5 + 0.5) * u_viewport;
  vec2 d = pHi - pLo;
  float dl = length(d);
  vec2 dir = dl > 1e-4 ? d / dl : vec2(1.0, 0.0);
  vec2 nrm = vec2(-dir.y, dir.x);

  float width = s1.y;
${
  glow
    ? `  float sigma = max(width * u_glowSigma, u_glowMin) * u_dpr;
  float r = sigma * 3.0 + 1.0;`
    : `  float core = (width + (s0.w >= 0.0 ? ${f(PACKET_WIDEN_PX)} : 0.0)) * 0.5;
  float shade = u_shadePx > 0.0 ? (width + u_shadePx) * 0.5 + 0.5 : 0.0;
  float r = max(core, shade) * u_dpr + 1.5;`
}

  vec2 corner = vec2(float(gl_VertexID & 1) * 2.0 - 1.0, float(gl_VertexID >> 1) * 2.0 - 1.0);
  vec2 base = corner.x < 0.0 ? pLo - dir * r : pHi + dir * r;
  vec2 px = base + nrm * (corner.y * r);

  v_px = px;
  v_seg = vec4(pLo, pHi);
  v_wm = vec4(cLo.w, cHi.w, mLo, mHi);
  v_s0 = s0;
  v_s1 = s1;
  v_s2 = s2;
  gl_Position = vec4(px / u_viewport * 2.0 - 1.0, 0.0, 1.0);
}
`;

/** Shared capsule maths: distance to the visible segment + metres at the pixel. */
const CAPSULE_COMMON = /* glsl */ `
in vec2 v_px;
flat in vec4 v_seg;
flat in vec4 v_wm;
flat in vec4 v_s0;
flat in vec4 v_s1;
flat in vec4 v_s2;

uniform float u_dpr;
uniform float u_trailM;     // 480 CSS px expressed in metres at the current zoom

float g_dist;
float g_m;
float g_packet;
float g_trail;

void capsule() {
  vec2 p0 = v_seg.xy;
  vec2 d = v_seg.zw - p0;
  float l2 = dot(d, d);
  float t = l2 > 1e-6 ? clamp(dot(v_px - p0, d) / l2, 0.0, 1.0) : 0.0;
  g_dist = length(v_px - (p0 + d * t));
  // Screen fraction t → ground fraction s (perspective-correct).
  float s = t * v_wm.x / max(v_wm.y * (1.0 - t) + v_wm.x * t, 1e-6);
  g_m = mix(v_wm.z, v_wm.w, s);
  float z = (g_m - v_s0.w) / max(v_s1.x, 1.0);
  g_packet = v_s0.w >= 0.0 ? exp(-0.5 * z * z) : 0.0;
  g_trail = smoothstep(0.0, max(u_trailM, 1.0), v_s0.x - g_m) * v_s2.x;
}
`;

const LINE_FS = /* glsl */ `${HEADER}
${CAPSULE_COMMON}
uniform float u_shadePx;
uniform vec3 u_color[5];    // rest colour per style: hero bloom preview site ring
uniform vec3 u_trail;
uniform vec3 u_comet;

layout(location = 0) out vec4 o_core;
layout(location = 1) out vec4 o_shade;

void main() {
  capsule();
  float width = v_s1.y + ${f(PACKET_WIDEN_PX)} * g_packet;
  float cov = clamp(width * u_dpr * 0.5 + 0.5 - g_dist, 0.0, 1.0);
  float sh = 0.0;
  if (u_shadePx > 0.0) {
    float hs = (v_s1.y + u_shadePx) * u_dpr * 0.5;
    sh = clamp((hs - g_dist) / u_dpr + 0.5, 0.0, 1.0); // 1 CSS px feather
  }
  if (cov <= 0.0 && sh <= 0.0) discard;
  int style = clamp(int(v_s1.w + 0.5), 0, 4);
  vec3 c = mix(u_color[style], u_trail, g_trail);
  c = mix(c, u_comet, ${f(PACKET_BRIGHTEN)} * g_packet);
  float a = cov * v_s0.z;
  o_core = vec4(c * a, a);
  o_shade = vec4(sh * v_s0.z, 0.0, 0.0, 0.0);
}
`;

const GLOW_FS = /* glsl */ `${HEADER}
${CAPSULE_COMMON}
uniform vec3 u_glow;
uniform float u_glowSigma;
uniform float u_glowMin;
out vec4 o_glow;

void main() {
  capsule();
  float sigma = max(v_s1.y * u_glowSigma, u_glowMin) * u_dpr;
  float g = exp(-0.5 * (g_dist * g_dist) / (sigma * sigma));
  // Light is strongest where it has just arrived: the far trail glows a little less.
  float k = ${f(GLOW_PEAK)} * g * v_s1.z * v_s0.z * (1.0 - 0.45 * g_trail) * (1.0 + 0.6 * g_packet);
  if (k <= 0.002) discard;
  o_glow = vec4(u_glow * k, k);
}
`;

const FULLSCREEN_VS = /* glsl */ `${HEADER}
void main() {
  vec2 p = vec2(float((gl_VertexID << 1) & 2), float(gl_VertexID & 2));
  gl_Position = vec4(p * 2.0 - 1.0, 0.0, 1.0);
}
`;

const COMPOSITE_LINE_FS = /* glsl */ `${HEADER}
uniform sampler2D u_core;
uniform sampler2D u_shadeTex;
uniform vec4 u_shade;       // straight rgb + alpha of --ld-route-shade
uniform int u_bridge;       // widest shaded gap between two cores that closes into one band (device px; 0 = off)
out vec4 o;

// The first core texel (alpha > .5) within u_bridge steps along d, or vec4(0).
vec4 coreAlong(ivec2 ij, ivec2 d, ivec2 hi) {
  for (int s = 1; s <= ${BRIDGE_MAX_PX}; s++) {
    if (s > u_bridge) break;
    vec4 t = texelFetch(u_core, clamp(ij + d * s, ivec2(0), hi), 0);
    if (t.a > 0.5) return t;
  }
  return vec4(0.0);
}

void main() {
  ivec2 ij = ivec2(gl_FragCoord.xy);
  vec4 c = texelFetch(u_core, ij, 0);
  float sh = texelFetch(u_shadeTex, ij, 0).r;
  // Routes that share a road are one lit band: a shaded gap with a core on both sides along one axis
  // takes the cores' light instead of the shade, so two lines a few px apart never read as rails (L1-15).
  if (u_bridge > 0 && sh > 0.0 && c.a < 0.5) {
    ivec2 hi = textureSize(u_core, 0) - 1;
    ivec2 dirs[4] = ivec2[4](ivec2(1, 0), ivec2(0, 1), ivec2(1, 1), ivec2(1, -1));
    for (int k = 0; k < 4; k++) {
      vec4 a = coreAlong(ij, dirs[k], hi);
      if (a.a <= 0.5) continue;
      vec4 b = coreAlong(ij, -dirs[k], hi);
      if (b.a <= 0.5) continue;
      c = max(c, 0.5 * (a + b));
      break;
    }
  }
  float sa = sh * u_shade.a * (1.0 - c.a);
  o = vec4(c.rgb + u_shade.rgb * sa, c.a + sa);
}
`;

const COMPOSITE_GLOW_FS = /* glsl */ `${HEADER}
uniform sampler2D u_glowTex;
uniform vec2 u_full;        // full-res drawing-buffer size (uv = fragment / size: the same NDC the capsules used)
uniform float u_gain;
out vec4 o;
void main() {
  vec3 g = texture(u_glowTex, gl_FragCoord.xy / u_full).rgb * u_gain;
  o = vec4(min(g, vec3(1.0)), 0.0);
}
`;

/** Comet instances: a_pos (origin-relative Mercator), a_p = coreDiam px, haloDiam px, alpha, scale. */
const COMET_VS = /* glsl */ `${HEADER}
layout(location = 0) in vec2 a_pos;
layout(location = 1) in vec4 a_p;
uniform mat4 u_matrix;
uniform vec2 u_viewport;
uniform float u_dpr;
uniform float u_near;
out vec2 v_off;
flat out vec4 v_p;
void main() {
  vec4 c = u_matrix * vec4(a_pos, 0.0, 1.0);
  v_p = a_p;
  if (c.w < u_near || a_p.z <= 0.0005) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); v_off = vec2(0.0); return; }
  vec2 sp = (c.xy / c.w * 0.5 + 0.5) * u_viewport;
  float r = max(a_p.x, a_p.y) * 0.5 * a_p.w * u_dpr + 1.5;
  vec2 corner = vec2(float(gl_VertexID & 1) * 2.0 - 1.0, float(gl_VertexID >> 1) * 2.0 - 1.0);
  v_off = corner * r;
  gl_Position = vec4((sp + v_off) / u_viewport * 2.0 - 1.0, 0.0, 1.0);
}
`;

const COMET_FS = /* glsl */ `${HEADER}
in vec2 v_off;
flat in vec4 v_p;
uniform vec3 u_cometColor;
uniform vec3 u_halo;
uniform float u_dpr;
out vec4 o;
void main() {
  float d = length(v_off);
  float rc = v_p.x * 0.5 * v_p.w * u_dpr;
  float core = clamp(rc + 0.5 - d, 0.0, 1.0);
  float rh = v_p.y * 0.5 * v_p.w * u_dpr;
  float h = 0.0;
  if (rh > 0.0) {
    float z = d / (rh * 0.42);
    h = exp(-0.5 * z * z) * (1.0 - smoothstep(rh * 0.78, rh, d)) * ${f(HALO_INTENSITY)};
  }
  vec4 halo = vec4(u_halo * h, h * ${f(HALO_OCCLUSION)});
  vec4 col = vec4(u_cometColor, 1.0) * core + halo * (1.0 - core);
  if (col.a <= 0.0 && dot(col.rgb, vec3(1.0)) <= 0.0) discard;
  o = col * v_p.z;
}
`;

export const SHADERS = {
  lineVS: CAPSULE_VS(false),
  lineFS: LINE_FS,
  glowVS: CAPSULE_VS(true),
  glowFS: GLOW_FS,
  fullscreenVS: FULLSCREEN_VS,
  compositeLineFS: COMPOSITE_LINE_FS,
  compositeGlowFS: COMPOSITE_GLOW_FS,
  cometVS: COMET_VS,
  cometFS: COMET_FS,
} as const;
